import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';

const patents = [
  { ref: 'GB2415465.0', title: 'Pay-AI: Comprehensive Automated Fueling System', date: '21 October 2024' },
  { ref: 'GB2507388.3', title: 'Vision-Based Target-Oriented Adaptive Control Framework for Autonomous Systems', date: '13 May 2025' },
  { ref: 'GB2507837.9', title: 'Helais Autonomous Aerial Support System', date: '20 May 2025' },
  { ref: 'GB2511230.1', title: 'Talon Autonomous Vehicle-Mounted UAV System', date: '27 May 2025' },
  { ref: 'GB2511317.6', title: 'System and Method for Reinforced Probabilistic Optimisation of Discrete Selections', date: '13 July 2025' },
];

const trademarks = [
  { mark: 'RLK', reg: 'UK00004012281', classes: '9, 12' },
  { mark: 'RLK, figurative', reg: 'UK00004038572', classes: '7, 9, 12, 13, 37, 42, 45' },
  { mark: 'AI-AV', reg: 'UK00004047302', classes: '7, 9, 12, 13, 39, 41, 45' },
  { mark: 'Helais', reg: 'UK00004201532', classes: '9, 12' },
  { mark: 'UrbanAnalytix', reg: 'UK00004011910', classes: '9' },
  { mark: 'PAY-AI', reg: 'UK00004021731', classes: '9, 36' },
  { mark: 'PAY-I', reg: 'UK00004021736', classes: '9, 36' },
];

const designs = [
  { ref: '6411603', title: 'Unmanned Aerial Vehicles', filed: '16 December 2024', granted: '20 January 2025' },
];

const IntellectualPropertyPage = () => {
  const [filter, setFilter] = useState('all');
  const filters = ['all', 'patents', 'trademarks', 'designs'];

  return (
    <Layout>
      <SEO
        title="Intellectual Property | Sandend House"
        description="Explore filed patent applications, registered trademarks and registered designs held across The RLK Group portfolio."
        path="/intellectual-property"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Intellectual Property",
          "url": "https://theknox.co.uk/intellectual-property",
          "isPartOf": { "@id": "https://theknox.co.uk/#website" }
        }}
      />

      {/* Hero */}
      <section className="py-20 lg:py-28 border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <p className="eyebrow mb-4">Intellectual Property</p>
          <h1 className="font-serif text-4xl lg:text-5xl mb-8" style={{ maxWidth: '600px' }} data-testid="ip-heading">
            Protecting what the portfolio creates.
          </h1>
          <div className="reading-width">
            <p className="text-[var(--sh-charcoal)] text-lg leading-relaxed mb-4">
              Intellectual property is treated as a core long-term asset.
            </p>
            <p className="text-[var(--sh-grey)] leading-relaxed">
              Across The RLK Group, the portfolio includes filed patent applications, registered trademarks and a registered design protecting technologies in artificial intelligence, autonomy, simulation, operational systems and associated products.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white border-b border-[var(--sh-rule-light)]">
        <div className="section-container flex flex-wrap gap-12">
          <div data-testid="ip-stat-patents">
            <div className="font-serif text-4xl text-[var(--sh-ink)]">{patents.length}</div>
            <p className="text-[var(--sh-grey)] text-sm mt-1">Filed patent applications</p>
          </div>
          <div data-testid="ip-stat-trademarks">
            <div className="font-serif text-4xl text-[var(--sh-ink)]">{trademarks.length}</div>
            <p className="text-[var(--sh-grey)] text-sm mt-1">Registered trademarks</p>
          </div>
          <div data-testid="ip-stat-designs">
            <div className="font-serif text-4xl text-[var(--sh-ink)]">{designs.length}</div>
            <p className="text-[var(--sh-grey)] text-sm mt-1">Registered design</p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-b border-[var(--sh-rule-light)]">
        <div className="section-container flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm rounded-sm transition-colors capitalize ${filter === f ? 'bg-[var(--sh-ink)] text-white' : 'bg-transparent text-[var(--sh-grey)] hover:text-[var(--sh-ink)]'}`}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All' : f === 'patents' ? 'Filed Patent Applications' : f === 'trademarks' ? 'Registered Trademarks' : 'Registered Designs'}
            </button>
          ))}
        </div>
      </section>

      {/* Patents */}
      {(filter === 'all' || filter === 'patents') && (
        <section className="py-16 lg:py-20 border-b border-[var(--sh-rule-light)]" data-testid="patents-section">
          <div className="section-container">
            <h2 className="font-serif text-2xl mb-8">Filed Patent Applications</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="patents-table">
                <thead>
                  <tr className="border-b border-[var(--sh-rule)]">
                    <th className="py-3 pr-6 text-xs font-semibold uppercase tracking-wider text-[var(--sh-grey)]">Reference</th>
                    <th className="py-3 pr-6 text-xs font-semibold uppercase tracking-wider text-[var(--sh-grey)]">Title</th>
                    <th className="py-3 text-xs font-semibold uppercase tracking-wider text-[var(--sh-grey)]">Filing Date</th>
                  </tr>
                </thead>
                <tbody>
                  {patents.map((p, i) => (
                    <tr key={i} className="border-b border-[var(--sh-rule-light)]">
                      <td className="py-4 pr-6 text-sm font-mono text-[var(--sh-grey)]">{p.ref}</td>
                      <td className="py-4 pr-6 text-sm text-[var(--sh-charcoal)]">{p.title}</td>
                      <td className="py-4 text-sm text-[var(--sh-grey)] whitespace-nowrap">{p.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Trademarks */}
      {(filter === 'all' || filter === 'trademarks') && (
        <section className="py-16 lg:py-20 bg-white border-b border-[var(--sh-rule-light)]" data-testid="trademarks-section">
          <div className="section-container">
            <h2 className="font-serif text-2xl mb-8">Registered Trademarks</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="trademarks-table">
                <thead>
                  <tr className="border-b border-[var(--sh-rule)]">
                    <th className="py-3 pr-6 text-xs font-semibold uppercase tracking-wider text-[var(--sh-grey)]">Mark</th>
                    <th className="py-3 pr-6 text-xs font-semibold uppercase tracking-wider text-[var(--sh-grey)]">Registration</th>
                    <th className="py-3 text-xs font-semibold uppercase tracking-wider text-[var(--sh-grey)]">Classes</th>
                  </tr>
                </thead>
                <tbody>
                  {trademarks.map((t, i) => (
                    <tr key={i} className="border-b border-[var(--sh-rule-light)]">
                      <td className="py-4 pr-6 text-sm text-[var(--sh-charcoal)] font-medium">{t.mark}</td>
                      <td className="py-4 pr-6 text-sm font-mono text-[var(--sh-grey)]">{t.reg}</td>
                      <td className="py-4 text-sm text-[var(--sh-grey)]">{t.classes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Designs */}
      {(filter === 'all' || filter === 'designs') && (
        <section className="py-16 lg:py-20 border-b border-[var(--sh-rule-light)]" data-testid="designs-section">
          <div className="section-container">
            <h2 className="font-serif text-2xl mb-8">Registered Designs</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left" data-testid="designs-table">
                <thead>
                  <tr className="border-b border-[var(--sh-rule)]">
                    <th className="py-3 pr-6 text-xs font-semibold uppercase tracking-wider text-[var(--sh-grey)]">Reference</th>
                    <th className="py-3 pr-6 text-xs font-semibold uppercase tracking-wider text-[var(--sh-grey)]">Title</th>
                    <th className="py-3 pr-6 text-xs font-semibold uppercase tracking-wider text-[var(--sh-grey)]">Filed</th>
                    <th className="py-3 text-xs font-semibold uppercase tracking-wider text-[var(--sh-grey)]">Granted</th>
                  </tr>
                </thead>
                <tbody>
                  {designs.map((d, i) => (
                    <tr key={i} className="border-b border-[var(--sh-rule-light)]">
                      <td className="py-4 pr-6 text-sm font-mono text-[var(--sh-grey)]">{d.ref}</td>
                      <td className="py-4 pr-6 text-sm text-[var(--sh-charcoal)]">{d.title}</td>
                      <td className="py-4 pr-6 text-sm text-[var(--sh-grey)] whitespace-nowrap">{d.filed}</td>
                      <td className="py-4 text-sm text-[var(--sh-grey)] whitespace-nowrap">{d.granted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Legal notice */}
      <section className="py-12 bg-[var(--sh-stone)]/30 border-b border-[var(--sh-rule-light)]">
        <div className="section-container">
          <p className="text-[var(--sh-grey)] text-xs leading-relaxed max-w-3xl">
            The intellectual-property information on this page is provided as a high-level public summary. Registration and filing status should be confirmed through the relevant official register. All rights are reserved by the relevant owning entity.
          </p>
        </div>
      </section>

      {/* Licensing CTA */}
      <section className="py-16 lg:py-20">
        <div className="section-container">
          <h2 className="font-serif text-2xl mb-4">Intellectual-property and licensing enquiries</h2>
          <p className="text-[var(--sh-grey)] mb-6 reading-width">
            For relevant licensing, commercialisation or intellectual-property enquiries, contact Sandend House or the relevant operating company.
          </p>
          <Link to="/contact" className="btn-primary" data-testid="ip-contact-cta">
            Contact Us <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default IntellectualPropertyPage;
